import React, { useId } from "react";
import { formatSlotRange, groupByPeriod } from "./slotGrid";

/** A tile's state, in priority order: a slot that has started is past whatever else is true. */
const stateOf = (slot, now) => {
  if (slot.startsAt.getTime() < now.getTime()) return "past";
  if (slot.booking) return "booked";
  return "open";
};

const TILE_CLASS = {
  open: "border-green/30 bg-white text-bc-text",
  // Grey, but not struck through: a booked slot is a call that is happening,
  // and a strikethrough reads as one that was cancelled.
  booked: "border-gray-200 bg-light-gray text-bc-faintest",
  past: "border-gray-200 bg-light-gray/50 text-bc-faintest",
};

const SR_SUFFIX = {
  open: ", open to book",
  booked: ", booked",
  past: ", already passed",
};

/**
 * One call tier's slots on one day — a section of the View slots dialog.
 *
 * The slots are **generated here, not stored anywhere**: `shiftSlotsOnDate`
 * walks the day's shifts with this tier's duration and buffer, exactly as the
 * server does when a client asks for slots, and marks each against the
 * coach's live bookings with the server's own buffer-widened overlap. So this
 * is a preview of what a client on this tier would be offered, not a list the
 * coach published.
 *
 * Three states: open, booked (a live booking overlaps it, buffers included),
 * and past (it has already started — the server stops offering a slot then).
 *
 * @param {Object} props
 * @param {string} props.title - The tier, e.g. "First call".
 * @param {string} props.detail - e.g. "45 min call · 15 min buffer".
 * @param {Array<{ startsAt: Date, endsAt: Date, booking: Object|null }>} props.slots
 * @param {Date} [props.now]
 */
export const SlotPreviewList = ({ title, detail, slots, now = new Date() }) => {
  const headingId = useId();
  const list = (Array.isArray(slots) ? slots : []).map((slot) => ({
    ...slot,
    state: stateOf(slot, now),
  }));
  const open = list.filter((slot) => slot.state === "open").length;
  const groups = groupByPeriod(list);

  return (
    <section aria-labelledby={headingId} className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 id={headingId} className="text-sm font-semibold text-bc-text">
            {title}
          </h3>
          <p className="mt-0.5 text-xs text-bc-soft">{detail}</p>
        </div>
        <span className="rounded-full bg-light-orange px-3 py-1 text-xs font-medium text-orange-primary">
          {open} of {list.length} open
        </span>
      </div>

      {list.length === 0 ? (
        <p className="mt-3 text-xs text-bc-soft">
          No call of this length fits in this day's shifts.
        </p>
      ) : (
        <div className="mt-3 space-y-4">
          {groups
            .filter((group) => group.slots.length > 0)
            .map((group) => (
              <div key={group.id}>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-bc-text-2">
                  {group.label}{" "}
                  <span className="font-normal normal-case text-bc-faint">{group.caption}</span>
                </p>
                <ul className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                  {group.slots.map((slot) => (
                    <li key={slot.startsAt.getTime()}>
                      {/*
                        `relative` is load-bearing: the sr-only child is
                        position:absolute, and without a positioned ancestor it
                        escapes main's overflow clipping and stretches the page.
                      */}
                      <span
                        className={`relative flex min-h-[40px] items-center justify-center rounded-lg border px-2 text-xs ${
                          TILE_CLASS[slot.state]
                        }`}
                      >
                        {formatSlotRange(slot)}
                        <span className="sr-only">{SR_SUFFIX[slot.state]}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </div>
      )}
    </section>
  );
};

/** The key under the tier sections — rendered once per dialog, not per tier. */
export const SlotPreviewLegend = () => (
  <div className="flex flex-wrap items-center gap-4 text-[11px] text-bc-soft">
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-sm border border-green/40 bg-white" aria-hidden="true" />
      Open to book
    </span>
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-sm bg-light-gray" aria-hidden="true" />
      Booked
    </span>
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-sm bg-light-gray/50" aria-hidden="true" />
      Past
    </span>
  </div>
);
